import { useState, memo } from "react";
import { EmptyState, v } from "../../ui";
import { fmtMoney } from "./money";

/**
 * ExpenseChart — SVG doughnut chart showing expenses by category.
 *
 * Props:
 *   data — array of { category: string, amount: number } sorted desc
 */

// The pastel category palette (design-brief 1), each segment outlined in ink.
const PALETTE = [
  "pastel-blue", "pastel-green", "pastel-yellow", "pastel-pink", "pastel-lilac",
  "pastel-orange", "pastel-mint", "pastel-periwinkle", "pastel-coral", "pastel-neutral",
];

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, outerR, innerR, startDeg, endDeg) {
  const p1 = polarToCartesian(cx, cy, outerR, startDeg);
  const p2 = polarToCartesian(cx, cy, outerR, endDeg);
  const p3 = polarToCartesian(cx, cy, innerR, endDeg);
  const p4 = polarToCartesian(cx, cy, innerR, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`,
    `L ${p3.x.toFixed(2)} ${p3.y.toFixed(2)}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${p4.x.toFixed(2)} ${p4.y.toFixed(2)}`,
    "Z",
  ].join(" ");
}


function ExpenseChart({ data }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  if (!data || data.length === 0) {
    return <EmptyState title="No expenses for this period" />;
  }

  const total = data.reduce((s, d) => s + d.amount, 0);
  if (total === 0) {
    return <EmptyState title="No expenses for this period" />;
  }

  // ── Build arc segments ────────────────────────────────────────────────────
  const CX = 100, CY = 100, OUTER = 90, INNER = 52;
  let cursor = 0;
  const segments = data.map((d, i) => {
    const startDeg = cursor;
    const sweep    = (d.amount / total) * 360;
    const endDeg   = cursor + sweep;
    cursor = endDeg;
    const isHovered = hoveredIdx === i;
    return {
      ...d,
      color: v(PALETTE[i % PALETTE.length]),
      path: arcPath(CX, CY, isHovered ? OUTER + 6 : OUTER, INNER, startDeg, endDeg),
      pct: ((d.amount / total) * 100).toFixed(1),
    };
  });

  return (
    <div className="fin-donut">
      {/* Doughnut */}
      <svg viewBox="0 0 200 200" className="fin-donut__svg" aria-label="Expense breakdown doughnut chart">
        {segments.map((seg, i) => (
          <path
            key={i}
            d={seg.path}
            className="fin-donut__seg"
            style={{ fill: seg.color, opacity: hoveredIdx == null || hoveredIdx === i ? 1 : 0.6 }}
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
          />
        ))}
        {/* Centre label */}
        <text x={CX} y={CY - 6} textAnchor="middle" className="fin-donut__big">
          {hoveredIdx != null ? `${segments[hoveredIdx].pct}%` : "Total"}
        </text>
        <text x={CX} y={CY + 12} textAnchor="middle" className="fin-donut__small">
          {hoveredIdx != null ? fmtMoney(segments[hoveredIdx].amount) : fmtMoney(total)}
        </text>
      </svg>

      {/* Legend */}
      <ul className="fin-donut__legend">
        {segments.map((seg, i) => (
          <li
            key={i}
            className={hoveredIdx === i ? "is-on" : undefined}
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
          >
            <span className="fin-donut__key" style={{ background: seg.color }} aria-hidden="true" />
            <span className="fin-donut__cat">{seg.category}</span>
            <span className="fin-donut__amt">{fmtMoney(seg.amount)}</span>
            <span className="fin-donut__pct">{seg.pct}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default memo(ExpenseChart);
